import React, { useState, useContext, useEffect } from 'react'
import { toast } from 'react-toastify';
import GenericModal from '../../../components/modal/GenericModal';
import { AuthContext } from '../../../hooks/context/authContext';
import { useManagePostRequest } from '../../../hooks/useManagePostRequest/useManagePostRequest';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { mapDropDownOptionsFromBackendToSelectOptionsNeededForLibrary } from '../../../helper/utils';
import { getIdEmpleadoSesion } from '../utils/sesion';
import AsyncSelectInput from '../../../components/Select/AsyncSelectInput';
import {
    crearConvocatoriaAPI,
    actualizarConvocatoriaAPI,
    getConvocatoriaAPI,
    getTPSedesAPI,
    getTPAllCargosAPI,
    getDataEmpleadoAPI,
    getEspecialidadActualEmpleadoAPI,
    getResponsableEmpleadoWithoutReserverdPeopleWordTypeAPI,
} from '../../../consumers/backendApisUrls';
import ModalEnabled from '../../../components/modalComponents/ModalEnabled';
import InputSelect from '../../../components/modalComponents/InputSelect';
import InputText from '../../../components/modalComponents/InputText';
import InputNumber from '../../../components/modalComponents/InputNumber';
import InputDate from '../../../components/modalComponents/InputDate';
import { aInicioDelDia, aFinDelDia } from './utils/convocatoriaRecap';
import InputTextArea from '../../../components/modalComponents/InputTextArea';
import SeleccionarVacanteModal from './SeleccionarVacanteModal';
import VerRequerimiento from '../../gestionRequerimientos/modales/VerRequerimiento';

// ──────────────────────────────────────────────────────────────────────────
// Modal "Nueva convocatoria — Individual" (boceto a mano compartido por
// Jody, 26/08/2026; ajustado el mismo día). A diferencia de "Masivo" (wizard
// de 5 pasos), este es un único formulario resumido: en Individual, GDH
// identifica directamente a un colaborador y salta convocatoria/postulación/
// evaluación, yendo directo a registrar las condiciones del cambio (ver
// HU-traslados-y-promociones-resumen.md). Por eso no hay Stepper aquí — es
// intencional, no un paso pendiente de dividir.
//
// Nota de mapeo a modelo de datos (modelo-datos-notas.md): "Vacante Destino"
// corresponde a `TP_Postulante.IdRequerimientoPersonal` — una fila de
// `RequerimientoPersonal` (Cuadro de Requerimientos), tabla hija de
// `Presupuesto`; "Nuevo Sueldo" = `NuevoSueldo` (decimal(18,2)), ya
// confirmado en el modelo. No hay campo "Headcount" separado (ver ajuste
// 28/08/2026 más abajo): al elegir la Vacante Destino ya queda implícito a
// qué Presupuesto/Headcount pertenece, así que mostrar ambos era redundante.
//
// Reglas del boceto:
// - "Buscar Empleado" usa `AsyncSelectInput` con el endpoint real de búsqueda
//   de empleados (`getResponsableEmpleadoWithoutReserverdPeopleWordTypeAPI`,
//   mismo que ReasignarResponsable.jsx). Al seleccionar un empleado se guarda
//   su Id en `formValues.IdEmpleado`. Ese endpoint solo devuelve Value/Text,
//   por lo que los datos extra (DNI/Cargo/Sede/Región/FechaIngreso) que antes
//   traía el mock ya no se muestran como etiquetas RecapField.
// - Al elegir un empleado (o cambiar el Tipo) se autocompleta "Convocatoria"
//   como "Convocatoria " + Tipo (Traslado/Promoción) + " Individual " +
//   Nombre del Empleado — ej. "Convocatoria Traslado Individual JUAN PEREZ
//   RIVAS". El usuario puede editarlo libremente después; si vuelve a
//   cambiar el Tipo o el empleado, se vuelve a autocompletar.
// - "Nuevo Sueldo" solo se muestra cuando TipoProceso="P" (Promoción) — a
//   pedido explícito de Jody. "Vacante Destino" en cambio se muestra
//   siempre, sin importar el Tipo (ver ajuste 28/08/2026 más abajo).
// - Modal más ancho (950px) con los campos editables en dos columnas para
//   evitar scroll vertical, a pedido de Jody. Comentarios queda a todo el
//   ancho por ser un campo más grande.
// - No hay botón "Cancelar" aparte de "Enviar": el boceto solo muestra
//   "Enviar", y GenericModal ya provee el ícono de cierre (X) en el header.
// - "Enviar" crea la convocatoria Individual (POST `CrearConvocatoria`) y luego
//   cierra el modal + refresca la grilla.
//
// Ajuste 28/08/2026 — campo "Vacante Destino" rediseñado:
// - Se quitó el campo "Headcount" (era el `<select>` de Presupuesto): es
//   redundante, porque la Vacante Destino elegida (RequerimientoPersonal) ya
//   pertenece implícitamente a un Presupuesto/Headcount — mostrar ambos
//   obligaba a mantenerlos sincronizados sin necesidad.
//   CONFIRMADO POR NEGOCIO (04/09/2026): el Headcount no es necesario; lo
//   requerido es IdRequerimientoPersonal, que lo sustituye. La divergencia
//   con HU-TR-01 CA-18 / R-14 (y HU-PR-01 R-14) es deliberada: corresponde
//   actualizar la HU, no reponer el campo.
// - "Vacante Destino" dejó de ser un `<select>` simple: ahora es un
//   InputText de solo lectura (muestra el Id de la vacante elegida) más
//   botones "Seleccionar vacante" / "Ver" (mutuamente excluyentes según haya
//   o no una vacante elegida) y "Eliminar vacante". "Seleccionar vacante"
//   abre `SeleccionarVacanteModal` (grilla filtrable, calcada de "Gestión de
//   Requerimientos"); "Ver" reutiliza el modal real y ya existente
//   `gestionRequerimientos/modales/VerRequerimiento.jsx` tal cual, pasándole
//   el Id elegido — a diferencia del resto de este formulario, ese modal SÍ
//   llama a un API real (`getEditRequerimientoPersonalAPI`), porque el
//   Cuadro de Requerimientos ya es un módulo existente y funcionando; lo
//   único mock de este flujo es la grilla de selección.
// - Corrección el mismo día: los controles de "Vacante Destino" (InputText +
//   botones) se muestran **siempre**, ya no solo cuando TipoProceso="P" —
//   Jody indicó que la elección de vacante no depende del Tipo de
//   convocatoria. Lo único que sigue condicionado a Promoción es "Nuevo
//   Sueldo".
// ──────────────────────────────────────────────────────────────────────────

const SECTION_NAME = "NuevaConvocatoriaIndividual";

const TIPO_PROCESO_OPTIONS = [
    { value: "T", label: "Traslado" },
    { value: "P", label: "Promoción" },
];

// Mock: reemplazar por la búsqueda real de empleados (AsyncSelectInput) cuando exista el API.
// DNI/Cargo/Sede/Región/FechaIngreso simulan lo que traería ese endpoint junto con el empleado.
// "Convocatoria" + Tipo (Traslado/Promoción) + "Individual" + Nombre del Empleado — regla explícita de Jody.
const buildNombreConvocatoria = (tipoProceso, nombreEmpleado) => {
    const tipoLabel = TIPO_PROCESO_OPTIONS.find(o => o.value === tipoProceso)?.label ?? "";
    const partes = ["Convocatoria", tipoLabel, "Individual", nombreEmpleado?.toUpperCase()].filter(Boolean);
    return partes.join(" ");
}

// CA-17: FechaIngresoEmpresa llega como ISO-8601 desde el .NET (DateTime?).
const formatFechaIngreso = (fechaIso) => {
    if (!fechaIso) return "-";
    const [anio, mes, dia] = fechaIso.split("T")[0].split("-");
    return `${dia}/${mes}/${anio}`;
}

const NuevaConvocatoriaIndividual = ({ toggleModal, refreshPage, convocatoria = null, modoEdicion = false }) => {
    const { state } = useContext(AuthContext);
    const [formValues, setFormValues] = useState({ TipoProceso: "T", NombreConvocatoria: buildNombreConvocatoria("T"), FechaEfectiva: "" });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalVacante, setModalVacante] = useState({ isEnable: false });
    const [sedeOptions, setSedeOptions] = useState([]);
    const [cargoOptions, setCargoOptions] = useState([]);
    const [nombreEmpleado, setNombreEmpleado] = useState("");
    const [empleadoDetalle, setEmpleadoDetalle] = useState({});
    const [managePostRequest] = useManagePostRequest();
    const [executeGet] = useGetRequest();

    useEffect(() => {
        (async () => {
            const [sedes, cargos] = await Promise.all([
                executeGet(getTPSedesAPI()),
                executeGet(getTPAllCargosAPI()),
            ]);
            setSedeOptions(mapDropDownOptionsFromBackendToSelectOptionsNeededForLibrary(sedes?.data ?? []));
            setCargoOptions(mapDropDownOptionsFromBackendToSelectOptionsNeededForLibrary(cargos?.data ?? []));
        })();
    }, []);

    // Precarga la cabecera desde el `row` camelCase de la grilla (Editar/Copiar).
    useEffect(() => {
        if (convocatoria) {
            setFormValues({
                TipoProceso: convocatoria.tipoProceso ?? "T",
                NombreConvocatoria: convocatoria.nombreConvocatoria ?? "",
                FechaEfectiva: convocatoria.fechaHoraInicio ? String(convocatoria.fechaHoraInicio).split("T")[0] : "",
            });
        }
    }, [convocatoria]);

    // 23/09/2026: el `row` de la grilla no trae los datos del postulante
    // (colaborador/vacante/sueldo/cargo) — GetConvocatoria/{id} ahora si los
    // trae (PascalCase, DTO fuertemente tipado del .NET) cuando TipoModalidad
    // es Individual, asi que se completan aparte, sin pisar la cabecera de
    // arriba. Aplica tanto a Editar como a Copiar (mismo `convocatoria` prop).
    useEffect(() => {
        if (!convocatoria?.idConvocatoria) return;
        (async () => {
            const res = await executeGet(getConvocatoriaAPI(convocatoria.idConvocatoria));
            const detalle = res?.data;
            if (!detalle) return;

            setFormValues(lastData => ({
                ...lastData,
                IdEmpleado: detalle.IdEmpleado ?? null,
                IdSedeDestino: detalle.IdSedeDestino ?? null,
                IdVacanteDestino: detalle.IdRequerimientoPersonal ?? null,
                NuevoSueldo: detalle.NuevoSueldo ?? "",
                IdCargoPromocion: detalle.IdCargoPromocion ?? null,
                Comentarios: detalle.Comentario ?? "",
            }));

            if (detalle.NombreEmpleado) {
                setNombreEmpleado(detalle.NombreEmpleado);
            }

            // CA-17: mismos datos de referencia que se muestran al elegir el
            // empleado en Nuevo (ver handleChangeEmpleado) — si el perfil no
            // tiene el UseCase Directorio la llamada falla y no se muestran.
            if (detalle.IdEmpleado) {
                try {
                    const empRes = await executeGet(getDataEmpleadoAPI(detalle.IdEmpleado));
                    const emp = empRes?.data;
                    if (emp) {
                        setEmpleadoDetalle(prev => ({
                            ...prev,
                            NumeroDocumento: emp.NumeroDocumento,
                            EspecialidadPrincipal: emp.EspecialidadPrincipal,
                            NombreCargo: emp.NombreCargo,
                            Sede: emp.Sede,
                            Departamento: emp.Departamento,
                            FechaIngresoEmpresa: emp.FechaIngresoEmpresa,
                        }));
                    }
                } catch (err) {
                    console.error("No se pudo cargar el detalle del empleado (CA-17):", err);
                }
                cargarEspecialidadActual(detalle.IdEmpleado);
            }
        })();
    }, [convocatoria]);

    // "Especialidad" del colaborador = la que tiene actualmente segun el Cuadro de
    // Requerimientos vigente (misma fuente que "Especialidad C.R." de Revision Inicial).
    // Directorio/GetDatosEmpleado solo trae EspecialidadPrincipal, que muchos empleados
    // no tienen cargada y dejaba el campo en "-": se usa como respaldo (ver el render).
    // Va aparte y se mezcla con el estado previo porque las dos llamadas son
    // independientes y pueden resolver en cualquier orden.
    const cargarEspecialidadActual = async (idEmpleado) => {
        try {
            const res = await executeGet(getEspecialidadActualEmpleadoAPI(idEmpleado));
            const especialidadActual = res?.data?.EspecialidadActual;
            if (especialidadActual) {
                setEmpleadoDetalle(prev => ({ ...prev, EspecialidadActual: especialidadActual }));
            }
        } catch (err) {
            console.error("No se pudo cargar la especialidad actual del empleado:", err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues(lastData => ({ ...lastData, [name]: value }));
    }

    // HU-PR-01 CA-19: cargo de destino de la promoción -> TP_Postulante.IdCargoPromocion
    const handleChangeCargoPromocion = (e) => {
        setFormValues(lastData => ({ ...lastData, IdCargoPromocion: e.target?.value?.value ?? e.target?.value }));
    }

    const handleChangeSede = (e) => {
        setFormValues(lastData => ({ ...lastData, IdSedeDestino: e.target?.value?.value ?? e.target?.value }));
    }

    const handleChangeTipo = (e) => {
        const nuevoTipo = e.target.value.value;
        setFormValues(lastData => ({
            ...lastData,
            TipoProceso: nuevoTipo,
            NombreConvocatoria: buildNombreConvocatoria(nuevoTipo, nombreEmpleado),
        }));
    }

    // AsyncSelectInput reenvia el value de react-select: `null` al limpiar,
    // el objeto {value,label} al elegir.
    const handleChangeEmpleado = (option) => {
        if (!option?.value) {
            setNombreEmpleado("");
            setEmpleadoDetalle({});
            setFormValues(lastData => ({ ...lastData, IdEmpleado: null, NombreConvocatoria: buildNombreConvocatoria(lastData.TipoProceso, "") }));
            return;
        }
        if (option?.value) {
            // Limpia lo del colaborador anterior: la especialidad actual llega por una
            // llamada aparte y, si esta tarda, no debe verse la del empleado previo.
            setEmpleadoDetalle({});
            setNombreEmpleado(option.label);
            setFormValues(lastData => ({
                ...lastData,
                IdEmpleado: option.value,
                NombreConvocatoria: buildNombreConvocatoria(lastData.TipoProceso, option.label),
            }));
            // CA-17: al seleccionar el empleado se recuperan sus datos vía
            // Directorio/GetDatosEmpleado — Nombre completo (viene del propio
            // AsyncSelect), DNI, Especialidad, Puesto (NombreCargo), Sede actual,
            // Región (Departamento) y Fecha de ingreso. Si el perfil no tiene el
            // UseCase Directorio la llamada falla y simplemente no se muestran
            // esos datos (no bloquea el alta).
            (async () => {
                try {
                    const res = await executeGet(getDataEmpleadoAPI(option.value));
                    const detalle = res?.data;
                    if (detalle) {
                        setEmpleadoDetalle(prev => ({
                            ...prev,
                            NumeroDocumento: detalle.NumeroDocumento,
                            EspecialidadPrincipal: detalle.EspecialidadPrincipal,
                            NombreCargo: detalle.NombreCargo,
                            Sede: detalle.Sede,
                            Departamento: detalle.Departamento,
                            FechaIngresoEmpresa: detalle.FechaIngresoEmpresa,
                        }));
                    }
                } catch (err) {
                    console.error("No se pudo cargar el detalle del empleado (CA-17):", err);
                }
            })();
            cargarEspecialidadActual(option.value);
        }
    }

    const isPromocion = formValues.TipoProceso === "P";

    // 30/09/2026, a pedido de Jody: en Promoción la vacante buscada debe ser del
    // NUEVO cargo (Cargo de Destino), así que "Seleccionar vacante" filtra por
    // Sede Destino Y Cargo de Destino — y ambos son obligatorios para poder abrir
    // la grilla (si falta alguno, se avisa en vez de abrir con un filtro
    // incompleto). En Traslado el cargo no cambia, así que se sigue filtrando
    // solo por Sede Destino, igual que antes (mismo patrón inverso que
    // AsignarVacante.jsx: ahí el cargo se prefiltra en Traslado con el cargo
    // ACTUAL del postulante, y en Promoción no se filtra por cargo porque
    // AsignarVacante no conoce el cargo de destino elegido).
    const handleAbrirSeleccionarVacante = () => {
        if (isPromocion && (!formValues.IdSedeDestino || !formValues.IdCargoPromocion)) {
            toast.warn("Debe seleccionar el cargo y sede de destino");
            return;
        }
        setModalVacante({
            isEnable: true,
            component: SeleccionarVacanteModal,
            data: {
                onSeleccionar: (vacante) => setFormValues(lastData => ({ ...lastData, IdVacanteDestino: vacante.IdRequerimientoPersonal })),
                sede: formValues.IdSedeDestino,
                idCargo: isPromocion ? formValues.IdCargoPromocion : null,
            },
        });
    }

    const handleVerVacante = () => {
        setModalVacante({ isEnable: true, component: VerRequerimiento, data: { id: formValues.IdVacanteDestino } });
    }

    const handleEliminarVacante = () => {
        setFormValues(lastData => ({ ...lastData, IdVacanteDestino: null }));
    }

    const idEmpleadoResponsable = getIdEmpleadoSesion(state);
    const numero = (v) => (v === "" || v === undefined || v === null) ? null : Number(v);

    // Alta directa del cambio (sin convocatoria/postulación/evaluación), ver
    // HU-traslados-y-promociones-resumen.md. "IdVacanteDestino" (frontend) =
    // TP_Postulante.IdRequerimientoPersonal (backend).
    //
    // CA-18: la "fecha efectiva del traslado" (campo FechaEfectiva, un único
    // InputDate "yyyy-mm-dd") se envia como FechaHoraInicio/FechaHoraFin. En
    // Individual es un cambio puntual, no una vigencia con inicio/fin, así que
    // ambos reciben la misma fecha: 00:00:00 el inicio y 23:59:59 el fin.
    const handleEnviar = () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        const fechaEfectiva = formValues.FechaEfectiva;
        const payload = {
            NombreConvocatoria: formValues.NombreConvocatoria,
            TipoProceso: formValues.TipoProceso,
            TipoModalidad: "I",
            IdEmpleadoResponsable: idEmpleadoResponsable,
            IdEmpleado: numero(formValues.IdEmpleado),
            IdSedeDestino: numero(formValues.IdSedeDestino),
            IdRequerimientoPersonal: numero(formValues.IdVacanteDestino),
            IdCargoPromocion: numero(formValues.IdCargoPromocion),
            NuevoSueldo: numero(formValues.NuevoSueldo),
            Comentarios: formValues.Comentarios,
            FechaHoraInicio: aInicioDelDia(fechaEfectiva),
            FechaHoraFin: aFinDelDia(fechaEfectiva),
        };
        managePostRequest(
            modoEdicion && convocatoria?.idConvocatoria ? actualizarConvocatoriaAPI(convocatoria.idConvocatoria) : crearConvocatoriaAPI(),
            payload,
            (response) => {
                refreshPage && refreshPage();
                toggleModal();
            },
            true,
            () => setIsSubmitting(false)
        );
    }

    return (
        <GenericModal showModal={toggleModal} titulo={modoEdicion ? "Editar Convocatoria — Individual" : "Nueva Convocatoria — Individual"} width="950px">
            <div className="modal-body">
                {/* refreshPage no-op: VerRequerimiento.jsx la llama solo si el usuario aprueba desde
                    ahí (acción real contra el Cuadro de Requerimientos) — no hay nada que refrescar
                    en este formulario por ese motivo. */}
                <ModalEnabled modalEnabled={modalVacante} setModalEnabled={setModalVacante} refreshPage={() => { }} />

                <div className="row">
                    <div className="col-sm-6">
                        <InputSelect label="Tipo" name="TipoProceso" sectionName={SECTION_NAME}
                            options={TIPO_PROCESO_OPTIONS}
                            value={TIPO_PROCESO_OPTIONS.find(o => o.value === formValues.TipoProceso)}
                            onChange={handleChangeTipo}
                            direction="column" isRequired isDisabled={modoEdicion} />
                    </div>
                    <div className="col-sm-6">
                        <label className="modal-label d-flex required">Buscar Empleado</label>
                        <AsyncSelectInput
                            api={getResponsableEmpleadoWithoutReserverdPeopleWordTypeAPI}
                            handleSelectInputChange={handleChangeEmpleado}
                            defaultValue={formValues.IdEmpleado ? { value: formValues.IdEmpleado, label: nombreEmpleado } : null}
                            placeholder="Buscar empleado..."
                            isDisabled={modoEdicion}
                        />
                    </div>
                </div>

                {/* CA-17: datos de referencia del colaborador seleccionado */}
                {Object.values(empleadoDetalle).some(Boolean) &&
                    <div className="row">
                        <div className="col-sm-4">
                            <div className="modal-label d-flex">D.N.I.: {empleadoDetalle.NumeroDocumento || "-"}</div>
                        </div>
                        <div className="col-sm-4">
                            <div className="modal-label d-flex">Puesto: {empleadoDetalle.NombreCargo || "-"}</div>
                        </div>
                        <div className="col-sm-4">
                            <div className="modal-label d-flex">Especialidad: {empleadoDetalle.EspecialidadActual || empleadoDetalle.EspecialidadPrincipal || "-"}</div>
                        </div>
                        <div className="col-sm-4">
                            <div className="modal-label d-flex">Sede actual: {empleadoDetalle.Sede || "-"}</div>
                        </div>
                        <div className="col-sm-4">
                            <div className="modal-label d-flex">Región: {empleadoDetalle.Departamento || "-"}</div>
                        </div>
                        <div className="col-sm-4">
                            <div className="modal-label d-flex">Fecha de ingreso: {formatFechaIngreso(empleadoDetalle.FechaIngresoEmpresa)}</div>
                        </div>
                    </div>
                }

                <hr />

                <div className="row">
                    <div className="col-sm-6">
                        <InputText label="Convocatoria" name="NombreConvocatoria" sectionName={SECTION_NAME}
                            value={formValues.NombreConvocatoria}
                            onChange={handleChange}
                            direction="column" isRequired />
                    </div>

                    <div className="col-sm-6">
                        <InputSelect label="Sede Destino" name="IdSedeDestino" sectionName={SECTION_NAME}
                            options={sedeOptions}
                            value={sedeOptions.find(o => o.value === formValues.IdSedeDestino)}
                            onChange={handleChangeSede}
                            direction="column" isRequired />
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-6">
                        <InputDate label={isPromocion ? "Fecha efectiva de la promoción" : "Fecha efectiva del traslado"} name="FechaEfectiva" sectionName={SECTION_NAME}
                            value={formValues.FechaEfectiva}
                            onChange={handleChange}
                            direction="column" isRequired />
                    </div>

                    {/* HU-PR-01 CA-19: el cargo de destino solo aplica a Promoción.
                        30/09/2026, a pedido de Jody: se movió acá (junto a Fecha
                        efectiva) desde su posición anterior debajo de Vacante Destino. */}
                    {isPromocion &&
                        <div className="col-sm-6">
                            <InputSelect label="Cargo de Destino" name="IdCargoPromocion" sectionName={SECTION_NAME}
                                options={cargoOptions}
                                value={cargoOptions.find(o => o.value === formValues.IdCargoPromocion)}
                                onChange={handleChangeCargoPromocion}
                                direction="column" isRequired />
                        </div>
                    }
                </div>

                <div className="row">
                    <div className={isPromocion ? "col-sm-8" : "col-sm-12"}>
                        <label className="modal-label d-flex required">Vacante Destino</label>
                        <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                            <div className="black-color" style={{ flex: 1 }}>
                                <input type="text" className="form-control" disabled
                                    placeholder="Ninguna vacante seleccionada"
                                    value={formValues.IdVacanteDestino ?? ""} />
                            </div>
                            {formValues.IdVacanteDestino
                                ? <button type="button" className="btn generic-button-buscador" onClick={handleVerVacante}>Ver</button>
                                : <button type="button" className="btn generic-button-buscador" onClick={handleAbrirSeleccionarVacante}>Seleccionar vacante</button>
                            }
                            <button type="button" className="btn generic-button-buscador" onClick={handleEliminarVacante}>Eliminar vacante</button>
                        </div>
                    </div>

                    {isPromocion &&
                        <div className="col-sm-4">
                            <InputNumber label="Nuevo Sueldo" name="NuevoSueldo" sectionName={SECTION_NAME}
                                value={formValues.NuevoSueldo}
                                onChange={handleChange}
                                direction="column" isRequired />
                        </div>
                    }
                </div>

                <InputTextArea label="Comentarios (Opcional)" name="Comentarios" sectionName={SECTION_NAME}
                    value={formValues.Comentarios}
                    onChange={handleChange}
                    direction="column" rows={3} />

                <div className="d-flex justify-content-center mt-4">
                    <button type="button" className="btn generic-button-buscador generic-button-hover-verde green-innova-background" disabled={isSubmitting} onClick={handleEnviar}>
                        Enviar
                    </button>
                </div>
            </div>
        </GenericModal>
    )
}

export default NuevaConvocatoriaIndividual
